import { useCallback, useEffect, useRef, useState } from 'react';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

export const UNAUTHORIZED_EVENT = 'studio:unauthorized';

async function request<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const response = await fetch(`/admin${path}`, {
    method: init.method ?? 'GET',
    credentials: 'same-origin',
    headers: init.body === undefined ? { 'x-studio-request': '1' } : { 'content-type': 'application/json', 'x-studio-request': '1' },
    ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
  });
  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    if (response.status === 401) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    throw new ApiError(response.status, String(payload['error'] ?? 'error'), String(payload['message'] ?? response.statusText), (payload['details'] as Record<string, unknown>) ?? {});
  }
  return payload as T;
}

export const api = {
  session: () => request<{ user: StudioUser | null }>('/session'),
  login: (email: string) => request<{ sent: boolean; devLink?: string }>('/auth/login', { method: 'POST', body: { email } }),
  verify: (token: string) => request<{ user: StudioUser }>('/auth/verify', { method: 'POST', body: { token } }),
  logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST', body: {} }),
  /** Runs one admin operation; the same names the companion will use. */
  op: async <T>(name: string, input: unknown = {}): Promise<T> => (await request<{ result: T }>(`/ops/${name}`, { method: 'POST', body: { input } })).result,
};

export interface StudioUser {
  email: string;
  name: string;
  permissions: string[];
  /** Every capability held somewhere; filters the sidebar and Today. */
  capabilities: string[];
  memberships: { roles: string[]; roleNames: string; capabilities: string[]; organization: { name: string }; brand: { name: string } | null; property: { name: string } | null }[];
}

export interface OpState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
}

/** Loads a read operation; reloads when the input (compared as JSON) or `reloadKey` changes. */
export function useOp<T>(name: string, input: unknown = {}, reloadKey: unknown = 0, enabled = true): OpState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [version, setVersion] = useState(0);
  const key = JSON.stringify(input);
  const latest = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    const call = ++latest.current;
    setLoading(true);
    api
      .op<T>(name, JSON.parse(key) as unknown)
      .then((result) => {
        if (call !== latest.current) return;
        setData(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (call !== latest.current) return;
        setError(err instanceof ApiError ? err : new ApiError(0, 'network', String(err)));
      })
      .finally(() => {
        if (call === latest.current) setLoading(false);
      });
  }, [name, key, version, enabled, reloadKey]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  return { data, error, loading, reload };
}

/** A short human message for an error from the API. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    const issues = error.details['issues'];
    if (Array.isArray(issues) && issues.length > 0) {
      const first = issues[0] as { path?: unknown[]; message?: string };
      return `${first.path?.join('.') ?? 'input'}: ${first.message ?? error.message}`;
    }
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}
